import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { LocalHospital, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const ShopByHealthConcern = () => {
  // Health concern data with image paths and display titles
  const healthConcerns = [
    {
      id: 1,
      image: '/assets/healthconcerns/bone,joint and muscle.png',
      title: 'Bone, Joint and Muscle',
      filename: 'bone,joint and muscle.png'
    },
    {
      id: 2,
      image: '/assets/healthconcerns/derma care.png',
      title: 'Derma Care',
      filename: 'derma care.png'
    },
    {
      id: 3,
      image: '/assets/healthconcerns/diabetes.png',
      title: 'Diabetes',
      filename: 'diabetes.png'
    },
    {
      id: 4,
      image: '/assets/healthconcerns/eye care.png',
      title: 'Eye Care',
      filename: 'eye care.png'
    },
    {
      id: 5,
      image: '/assets/healthconcerns/heart care.png',
      title: 'Heart Care',
      filename: 'heart care.png'
    },
    {
      id: 6,
      image: '/assets/healthconcerns/kidney care.png',
      title: 'Kidney Care',
      filename: 'kidney care.png'
    },
    {
      id: 7,
      image: '/assets/healthconcerns/liver care.png',
      title: 'Liver Care',
      filename: 'liver care.png'
    },
    {
      id: 8,
      image: '/assets/healthconcerns/respiratory care.png',
      title: 'Respiratory Care',
      filename: 'respiratory care.png'
    },
    {
      id: 9,
      image: '/assets/healthconcerns/stomach care.png',
      title: 'Stomach Care',
      filename: 'stomach care.png'
    }
  ];

  const handleHealthConcernClick = (title) => {
    // Optional: Add navigation or filtering logic here
    console.log('Health concern clicked:', title);
  };

  return (
    <Box sx={{ my: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: { xs: 0.5, sm: 0.75 },
          mb: { xs: 2, sm: 2.5, md: 3 },
          backgroundColor: 'primary.main',
          px: { xs: 1.25, sm: 1.5, md: 2 },
          py: { xs: 0.75, sm: 1, md: 1.25 },
          borderRadius: 1,
          width: '100%',
        }}
      >
        <LocalHospital sx={{ color: 'white', fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }} />
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 600,
            color: 'white',
            fontFamily: 'Lexend',
            textTransform: 'uppercase',
            textAlign: 'left',
            fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.875rem', lg: '1rem' },
          }}
        >
          Shop by Health Concern
        </Typography>
      </Box>
      
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          paddingX: '10px 0',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            px: { xs: 5, sm: 6, md: 7 },
            overflow: 'hidden',
          }}
        >
          <Swiper
            modules={[Navigation, Mousewheel]}
            spaceBetween={14}
            slidesPerView={2}
            slidesPerGroup={2}
            speed={800}
            freeMode={false}
            grabCursor={true}
            mousewheel={true}
            navigation={{
              nextEl: '.health-concern-next',
              prevEl: '.health-concern-prev',
            }}
            breakpoints={{
              480: {
                slidesPerView: 2.5,
                spaceBetween: 24,
              },
              640: {
                slidesPerView: 3,
                spaceBetween: 28,
              },
              768: {
                slidesPerView: 4,
                spaceBetween: 32,
              },
              1024: {
                slidesPerView: 5,
                spaceBetween: 36,
              },
              1280: {
                slidesPerView: 6,
                spaceBetween: 40,
              },
            }}
            style={{
              padding: '8px 0',
              margin: 0,
            }}
          >
          {healthConcerns.map((concern) => (
            <SwiperSlide key={concern.id} style={{ width: 'auto', height: 'auto' }}>
              <Box
                onClick={() => handleHealthConcernClick(concern.title)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 100, sm: 100, md: 120, lg: 140 },
                    height: { xs: 100, sm: 100, md: 120, lg: 140 },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    mb: { xs: 0.75, sm: 1 },
                    p: { xs: 0.5, sm: 0.75, md: 1 },
                    flexShrink: 0,
                    '&:hover': {
                      transform: 'scale(1.08)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={concern.image}
                    alt={concern.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                      display: 'block',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem', lg: '0.875rem' },
                    lineHeight: 1.3,
                    maxWidth: { xs: 100, sm: 120, md: 140, lg: 160 },
                    px: 0.5,
                    transition: 'color 0.2s ease-in-out',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {concern.title}
                </Typography>
              </Box>
            </SwiperSlide>
          ))}
          </Swiper>
        </Box>

        {/* Custom Navigation Buttons */}
        <IconButton
          className="health-concern-prev"
          sx={{
            position: 'absolute',
            left: { xs: 4, sm: 6, md: 8 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'white',
            boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15)',
            width: { xs: 32, sm: 36, md: 40 },
            height: { xs: 32, sm: 36, md: 40 },
            p: { xs: 0.75, sm: 1, md: 1.25 },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
              transform: 'translateY(-50%) scale(1.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.95)',
            },
            '&.swiper-button-disabled': {
              opacity: 0.25,
              cursor: 'not-allowed',
              '&:hover': {
                backgroundColor: 'white',
                color: 'inherit',
                transform: 'translateY(-50%)',
                boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
        </IconButton>

        <IconButton
          className="health-concern-next"
          sx={{
            position: 'absolute',
            right: { xs: 4, sm: 6, md: 8 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'white',
            boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15)',
            width: { xs: 32, sm: 36, md: 40 },
            height: { xs: 32, sm: 36, md: 40 },
            p: { xs: 0.75, sm: 1, md: 1.25 },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
              transform: 'translateY(-50%) scale(1.1)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.95)',
            },
            '&.swiper-button-disabled': {
              opacity: 0.25,
              cursor: 'not-allowed',
              '&:hover': {
                backgroundColor: 'white',
                color: 'inherit',
                transform: 'translateY(-50%)',
                boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <ChevronRight sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ShopByHealthConcern;

